import message from 'antd/lib/message'
import { FC, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'

import {
    BusinessAccountInviteStatus,
    useGetUserBusinessAccountStatusQuery,
} from '~/gql/graphql'
import logger from '~/lib/logger'
import { getUserIdentity } from '~/redux-modules/identity'

import JoinBanner from './JoinBanner'
import { determineBannerSubcategory } from './logic'
import SuccessBanner from './SuccessBanner'
import { BannerContext } from './useBannersContext'
import VerifyBanner from './VerifyBanner'

interface TeamsRequestAccessBannerProps {
    onShowingVerifyBanner?: (isShowing: boolean) => void
}

type BannerType = 'None' | 'Verify' | 'Success' | 'Join'

export const TeamsRequestAccessBanner: FC<TeamsRequestAccessBannerProps> = ({
    onShowingVerifyBanner,
}) => {
    const [hasJustJoined, setHasJustJoined] = useState(false)
    const [bannerToShow, setBannerToShow] = useState<BannerType>('None')

    const { id, email } = useSelector(getUserIdentity)

    const businessAccountStatusQuery = useGetUserBusinessAccountStatusQuery({
        variables: { userId: id },
    })

    useEffect(() => {
        const refetchData = async () => {
            try {
                await businessAccountStatusQuery.refetch({
                    userId: id,
                })
            } catch (e) {
                logger.exception(e, {
                    extra: {
                        info: 'My Account Page - failed to refetch businessAccountStatusQuery',
                    },
                })
                message.error(
                    'We encountered an error updating your Business Account Status.  Try refreshing the page!',
                )
            }
        }

        if (hasJustJoined) {
            refetchData()
        }
    }, [hasJustJoined, businessAccountStatusQuery, id])

    // determine which banner to show
    useEffect(() => {
        if (
            businessAccountStatusQuery.loading ||
            !businessAccountStatusQuery.data ||
            !businessAccountStatusQuery?.data?.getUserBusinessAccountStatus
        ) {
            setBannerToShow('None')
            return
        }

        if (businessAccountStatusQuery.error) {
            logger.exception(businessAccountStatusQuery.error, {
                extra: {
                    info: 'My Account Page - failed to refetch businessAccountStatusQuery',
                    variables: { userId: id },
                },
            })
            setBannerToShow('None')
            return
        }
        const {
            businessAccountId,
            isSalesforceContact,
            businessAccountInviteStatus,
        } = businessAccountStatusQuery.data.getUserBusinessAccountStatus

        if (!businessAccountId) {
            setBannerToShow('None')
        }

        if (
            isSalesforceContact &&
            businessAccountInviteStatus ===
                BusinessAccountInviteStatus.not_requested
        ) {
            setBannerToShow('Join')
        } else if (
            isSalesforceContact &&
            businessAccountInviteStatus ===
                BusinessAccountInviteStatus.pending_email_verification
        ) {
            setBannerToShow('Verify')
        } else if (
            hasJustJoined &&
            businessAccountInviteStatus === BusinessAccountInviteStatus.accepted
        ) {
            setBannerToShow('Success')
        }
    }, [businessAccountStatusQuery, hasJustJoined, id])

    // Anytime bannerToShow changes, alert parent component
    useEffect(() => {
        if (onShowingVerifyBanner) {
            if (bannerToShow === 'Verify') {
                onShowingVerifyBanner(true)
            } else {
                onShowingVerifyBanner(false)
            }
        }
    }, [bannerToShow, onShowingVerifyBanner])

    if (bannerToShow === 'None' || !businessAccountStatusQuery.data) {
        return null
    }

    let banner: JSX.Element | null = null

    function onFinishJoin() {
        setHasJustJoined(true)
    }

    switch (bannerToShow) {
        case 'Join':
            banner = <JoinBanner onFinishJoin={onFinishJoin} />
            break
        case 'Verify':
            banner = <VerifyBanner />
            break
        case 'Success':
            banner = <SuccessBanner />
    }

    if (!banner) {
        return null
    }

    const { isPOFeatureEnabled, isLTOFeatureEnabled, isTeamsFeatureEnabled } =
        businessAccountStatusQuery.data.getUserBusinessAccountStatus

    const bannerSubcategory = determineBannerSubcategory(
        !!isPOFeatureEnabled,
        !!isLTOFeatureEnabled,
        !!isTeamsFeatureEnabled,
    )

    if (bannerSubcategory === null) {
        return null
    }

    return (
        <BannerContext.Provider
            value={{
                ...businessAccountStatusQuery.data.getUserBusinessAccountStatus,
                userEmail: email,
                bannerSubcategory: bannerSubcategory,
            }}
        >
            <div data-testid="teams-request-access-banner">{banner}</div>
        </BannerContext.Provider>
    )
}
